// @flow

import React from 'react';
import moment from 'moment';
import pick from 'lodash/fp/pick';
import flow from 'lodash/fp/flow';
import values from 'lodash/fp/values';
import compact from 'lodash/fp/compact';
import toUpper from 'lodash/fp/toUpper';
import map from 'lodash/fp/map';
import prop from 'lodash/fp/prop';
import includes from 'lodash/fp/includes';
import flatten from 'lodash/fp/flatten';
import FileUpload from 'components/FileUpload/FileUpload';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import MaskSpinner from 'components/MaskSpinner/MaskSpinner';
import type {
  L10n,
  Time,
  ParticipantSearchResult,
  FileUploadResponse,
  VirtualEventStageResult,
} from 'type/type';

import FinishTimeInput from './FinishTimeInput';
import Error from './Error';

type Props = {
  l10n: L10n,
  participantInfo: ParticipantSearchResult,
  onSubmitResult: ({ stageResults: Array<VirtualEventStageResult>, fileId: string }) => void,
}

type State = {
  fileId: string,
  uploading: boolean,
  finishTime: Array<{
    name: string,
    time: Time,
  }>,
  fileNotChoose: boolean,
  timeIncomplete: boolean,
}

const routeStageMap = {
  TRIATHLON: [ 'SwimmingInTriathlon', 'Transition_1', 'CyclingInTriathlon', 'Transition_2', 'RunningInTriathlon' ],
  DUATHLON: [ 'StartRunningInDuathlon', 'Transition_1', 'CyclingInDuathlon', 'Transition_2', 'EndRunningInDuathlon' ],
};

const multiStageSportTypes = [ 'TRIATHLON', 'DUATHLON' ];

const buildFinishTime = (sportType: string) => {
  const isMultiStageSportType = includes(toUpper(sportType))(multiStageSportTypes);
  if (isMultiStageSportType) {
    return flow(
      prop(toUpper(sportType)),
      map(stage => ({
        name: stage,
        time: {},
      })),
    )(routeStageMap);
  }
  return [{ name: sportType, time: {} }];
};

const timeFilled = (finishTime: Array<{
  name: string,
  time: Time,
}>, stage: number) => {
  const filledValues = flow(
    map(flow(
      prop('time'),
      values,
      flatten,
    )),
    flatten,
    compact,
  )(finishTime);
  return filledValues.length === stage * 3;
};

const formateTime = (time: Time) => {
  const { hour, minutes, seconds } = pick([ 'hour', 'minutes', 'seconds' ])(time);
  return moment.duration(`${hour}:${minutes}:${seconds}`).toISOString();
};

const getResults = (finishTime: Array<{
  name: string,
  time: Time,
}>, stage: number) => {
  if (stage === 1) {
    return map(({ time }) => ({
      finalResult: formateTime(time),
    }))(finishTime);
  }

  return map(({ time, name }) => ({
    name,
    finalResult: formateTime(time),
  }))(finishTime);
};

const getStageForSportType = sportType => (includes(toUpper(sportType))(multiStageSportTypes)
  ? routeStageMap[toUpper(sportType)].length : 1);

export const SubmitComponent = class Submit extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      fileId: '',
      finishTime: buildFinishTime(prop('participantInfo.sportType')(props)),
      uploading: false,
      fileNotChoose: false,
      timeIncomplete: false,
    };
  }

  onFinishTimeChange = (name: string, time: Time) => {
    this.setState((
      { finishTime, timeIncomplete },
      { participantInfo: { sportType } },
    ) => {
      const stage = getStageForSportType(sportType);
      const finishTimeToSet = map(({ name: stageName, time: timeField }) => {
        if (stageName === name) {
          return { name, time };
        }
        return { name: stageName, time: timeField };
      })(finishTime);
      const allTimeFilled = timeFilled(finishTimeToSet, stage);

      return {
        finishTime: finishTimeToSet,
        timeIncomplete: allTimeFilled ? false : timeIncomplete,
      };
    });
  }

  onStartUpload = () => {
    this.setState({
      uploading: true,
      fileNotChoose: false,
    });
  }

  onUploadSuccess = ({
    token,
  }: FileUploadResponse) => {
    this.setState({
      uploading: false,
      fileId: token,
    });
  }

  onUploadFailed = () => {
    this.setState({
      uploading: false,
    });
  }

  onSubmit = () => {
    const { onSubmitResult, participantInfo: { sportType } } = this.props;
    const { fileId, finishTime } = this.state;
    const stage = getStageForSportType(sportType);
    const allTimeFilled = timeFilled(finishTime, stage);
    if (fileId && allTimeFilled) {
      const results = getResults(finishTime, stage);
      onSubmitResult({ fileId, stageResults: results });
    } else {
      this.setState({
        fileNotChoose: !fileId,
        timeIncomplete: !allTimeFilled,
      });
    }
  }

  render() {
    const {
      l10n: { formatMessage },
      participantInfo: {
        sportType,
        distanceName,
        firstName,
      },
    } = this.props;
    const {
      finishTime,
      uploading,
      timeIncomplete,
      fileNotChoose,
    } = this.state;

    return (
      <div className="virtual-event__submit-result__submit">
        {uploading && <MaskSpinner />}
        <div className="virtual-event__submit-result__submit__title">
          {formatMessage('page.event.virtualEvent.submitResult.submit.title', [ firstName ])}
        </div>
        <div className="virtual-event__submit-result__submit__subtitle">
          {formatMessage('page.event.virtualEvent.submitResult.submit.subtitle')}
        </div>
        <div className="virtual-event__submit-result__submit__category">
          <div className="virtual-event__submit-result__submit__category__label">
            {formatMessage('page.event.virtualEvent.submitResult.submit.category.title')}
          </div>
          <div className="virtual-event__submit-result__submit__category__content">
            {`${formatMessage(`global.sportsType.${sportType.toUpperCase()}`)} - ${distanceName}`}
          </div>
        </div>
        <div className="virtual-event__submit-result__submit__finish-time">
          <div className="virtual-event__submit-result__submit__finish-time__label">
            {formatMessage('page.event.virtualEvent.submitResult.submit.finishTime.title')}
          </div>
          <div className="virtual-event__submit-result__submit__finish-time__content">
            {finishTime.length > 1 && (
              <div className="virtual-event__submit-result__submit__tips">
                {formatMessage('page.event.virtualEvent.submitResult.submit.finishTime.tips')}
              </div>
            )}
            <FinishTimeInput
              finishTime={finishTime}
              highlightError={timeIncomplete}
              onFinishTimeChange={this.onFinishTimeChange} />
            {finishTime.length === 1 && (
              <div className="virtual-event__submit-result__submit__tips">
                {formatMessage('page.event.virtualEvent.submitResult.submit.finishTime.tips')}
              </div>
            )}
            {timeIncomplete && <Error message={formatMessage('page.event.virtualEvent.submitResult.submit.error.timeRequired')} />}
          </div>
        </div>
        <div className="virtual-event__submit-result__submit__upload">
          <div className="virtual-event__submit-result__submit__upload__label">
            {formatMessage('page.event.virtualEvent.submitResult.submit.upload.title')}
          </div>
          <div className="virtual-event__submit-result__submit__upload__content">
            <FileUpload
              buttonName={formatMessage('page.event.virtualEvent.submitResult.submit.upload.button.text')}
              acceptType=".jpg,.jpeg,.png,.pdf"
              fileMaxSize={10 * 1024 * 1024}
              uploadMessages={{
                uploading: formatMessage('page.event.virtualEvent.submitResult.submit.upload.uploading'),
                uploadFailed: formatMessage('page.event.virtualEvent.submitResult.submit.upload.error.failed'),
                fileTooLarge: formatMessage('page.event.virtualEvent.submitResult.submit.upload.error.tooLarge'),
                fileTypeError: formatMessage('page.event.virtualEvent.submitResult.submit.upload.error.invalidType'),
              }}
              onStartCallBack={this.onStartUpload}
              onSuccessCallBack={this.onUploadSuccess}
              onFailedCallBack={this.onUploadFailed}
              tips={(
                <div className="virtual-event__submit-result__submit__tips">
                  {formatMessage('page.event.virtualEvent.submitResult.submit.upload.tips')}
                </div>
              )} />
            {fileNotChoose && <Error message={formatMessage('page.event.virtualEvent.submitResult.submit.error.fileRequired')} />}
          </div>
        </div>
        <button
          onClick={this.onSubmit}
          type="button"
          className="virtual-event__submit-result__submit__button btn btn-primary">
          {formatMessage('page.event.virtualEvent.submitResult.submit.button.text')}
        </button>
      </div>
    );
  }
};

export default injectL10n()(SubmitComponent);
